import React, { useEffect, useState } from 'react';
import { Box, GridLegacy as Grid } from '@mui/material';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { SMBold, SMRegular, SMSemiBold } from '@/typography/Typography';
import { useTranslation } from 'react-i18next';
import { BankTransaction, CompanyAccount } from '@/services/PaymentsServiceV2';
import { useGetCompanyAccounts } from '@/services/paymentsQuery';
import { Dropdown } from '@/components/shared/Dropdown';
import { useFormatting } from '@/utils/formatting';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useErrorHandling } from '@/utils/errorHandling';

export interface CreateTopUpModelProps {
    selectedTransaction: BankTransaction | null;
    open: boolean;
    handleClose: () => void;
}

const CreateTopUpModel = ({
    selectedTransaction,
    open,
    handleClose
}: CreateTopUpModelProps) => {
    const { t } = useTranslation();

    const { data: companyAccounts } = useGetCompanyAccounts();

    const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>();

    const [selectedCompanyAccount, setSelectedCompanyAccount] =
        useState<CompanyAccount | null>();

    useEffect(() => {
        if (selectedCompanyId) {
            const account = companyAccounts?.data?.companyAccounts?.find(
                (account) => account.companyId === selectedCompanyId
            );
            setSelectedCompanyAccount(account);
        } else {
            setSelectedCompanyAccount(null);
        }
    }, [selectedCompanyId]);

    const { getBalanceLabel } = useFormatting();

    const { topUpCompanyAccountWithTransaction } = usePaymentsMutations();
    const { backendErrorHandler } = useErrorHandling();

    useEffect(() => {
        if (topUpCompanyAccountWithTransaction.error) {
            backendErrorHandler()(topUpCompanyAccountWithTransaction.error);
        }
    }, [topUpCompanyAccountWithTransaction.error]);

    const handleCancel = () => {
        setSelectedCompanyId('');
        handleClose?.();
    };

    useEffect(() => {
        if (topUpCompanyAccountWithTransaction.isSuccess) {
            handleCancel();
        }
    }, [topUpCompanyAccountWithTransaction.isSuccess]);

    return (
        <StyledDialog
            open={open}
            onClose={handleCancel}
            style={{
                overflow: 'visible'
            }}
        >
            <StyledDialogContainer
                style={{
                    width: '400px',
                    padding: '24px',
                    overflow: 'visible'
                }}
            >
                <Box m={1} mb={2} textAlign="center" width={'100%'}>
                    <SMSemiBold mb={1}>
                        {t('transaction_monitoring.create_top_up')}
                    </SMSemiBold>
                    <SMRegular>
                        {selectedTransaction?.bankTransactionRecordId}
                    </SMRegular>
                </Box>

                <Dropdown
                    label={'Select Company'}
                    style={{ marginBottom: '20px' }}
                    choices={companyAccounts?.data?.companyAccounts?.map(
                        (account) => ({
                            label: account.name,
                            value: account.companyId
                        })
                    )}
                    value={selectedCompanyId}
                    onChange={function (value: any | any[]): void {
                        setSelectedCompanyId(value as string);
                    }}
                />
                {selectedCompanyAccount && (
                    <Grid container>
                        <Grid container>
                            <Grid xs={4} item textAlign={'end'}>
                                <SMSemiBold>
                                    {t(
                                        'transaction_monitoring.current_balance'
                                    )}
                                </SMSemiBold>
                            </Grid>
                            <Grid xs={8} item textAlign={'end'}>
                                {getBalanceLabel(
                                    selectedCompanyAccount?.balance,
                                    selectedCompanyAccount?.currency,
                                    undefined,
                                    true
                                )}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid xs={4} item textAlign={'end'}>
                                <SMSemiBold>
                                    {t('transaction_monitoring.deposit_amount')}
                                </SMSemiBold>
                            </Grid>
                            <Grid xs={8} item textAlign={'end'}>
                                {getBalanceLabel(
                                    selectedTransaction?.amount,
                                    selectedCompanyAccount?.currency,
                                    undefined,
                                    true
                                )}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid xs={4} item textAlign={'end'}>
                                <SMSemiBold>
                                    {t('transaction_monitoring.new_balance')}
                                </SMSemiBold>
                            </Grid>
                            <Grid xs={8} item textAlign={'end'}>
                                {getBalanceLabel(
                                    (selectedTransaction?.amount ?? 0) +
                                        selectedCompanyAccount?.balance,
                                    selectedCompanyAccount?.currency,
                                    undefined,
                                    true
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        mt: 3
                    }}
                    gap={1.5}
                >
                    <OutlinedSecondaryButtonLG
                        fullWidth
                        color="secondary"
                        style={{
                            height: '40px',
                            borderRadius: '8px'
                        }}
                        onClick={handleCancel}
                    >
                        <SMBold textTransform={'none'}>{t('Cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>

                    <PrimaryButtonLG
                        fullWidth
                        size="large"
                        style={{ height: '39px' }}
                        id={'ignore-transaction'}
                        onClick={() => {
                            topUpCompanyAccountWithTransaction.mutate({
                                companyId: selectedCompanyId!,
                                bankTransactionRecordId:
                                    selectedTransaction?.bankTransactionRecordId!,
                                comment: `top up for ${selectedCompanyAccount?.name}`
                            });
                        }}
                    >
                        <SMBold textTransform={'none'}>
                            {t('transaction_monitoring.create_top_up')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Box>
            </StyledDialogContainer>
        </StyledDialog>
    );
};

export default CreateTopUpModel;
